/**
 * `/audits/:id/matrix` — repository-scoped items (`RepositoryMatrix.dc.html`, §7.3).
 *
 * A repository-scoped item is answered once per repository, and its overall state is the
 * **worst** of those answers: rejected beats pending beats partial beats verified. That
 * rule is the reason this screen exists — the workspace can show one state per item, but
 * only a grid can show where it came from.
 *
 * Two things the grid must never hide: `primaryRepoOnly` items have exactly one real
 * cell (the rest are struck through, not blank), and the roll-up label is the server's,
 * never recomputed here.
 */

import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { MarkerChip, StateChip, stateLabel } from '@portal/ui';
import type { ItemState } from '@portal/domain';
import type { Matrix } from '@portal/contracts';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const NOTE: CSSProperties = { fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 };
const MONO: CSSProperties = { fontFamily: 'var(--font-mono)' };

const CELL_W = 96;
const REF_W = 54;
const STATEMENT_W = 300;
const ROLLUP_W = 128;

/**
 * The six states in the order the workspace picker uses, so the numbers `1`–`6` mean the
 * same thing here as they do there.
 */
const STATES: readonly ItemState[] = [
  'PENDING',
  'PARTIAL',
  'VERIFIED',
  'REJECTED',
  'NOT_APPLICABLE',
  'WAIVED',
];

/** Short glyph text for a cell — the grid is too dense for a full chip per cell. */
const CELL_MARK: Record<ItemState, { text: string; fg: string; bg: string; bd: string }> = {
  VERIFIED: { text: '✓', fg: 'var(--ok)', bg: 'var(--okb)', bd: 'var(--ok-bd)' },
  PARTIAL: { text: '◐', fg: 'var(--wa)', bg: 'var(--wab)', bd: 'var(--wa-bd)' },
  REJECTED: { text: '✕', fg: 'var(--ba)', bg: 'var(--bab)', bd: 'var(--ba-bd)' },
  PENDING: { text: '○', fg: 'var(--mu)', bg: 'var(--neb)', bd: 'var(--bd)' },
  NOT_APPLICABLE: { text: '—', fg: 'var(--na)', bg: 'transparent', bd: 'var(--bd)' },
  WAIVED: { text: '⚑', fg: 'var(--wv)', bg: 'var(--wvb)', bd: 'var(--wv-bd)' },
};

export function AuditMatrixRoute() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ ref: string; repositoryId: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const matrix = useQuery({ queryKey: ['audit-matrix', id], queryFn: () => api.auditMatrix(id) });

  const patch = useMutation({
    mutationFn: (input: { ref: string; repositoryId: string; state: ItemState }) =>
      // version 0: the per-repo endpoint takes the row's own version; the read model does
      // not expose it yet, so the server's optimistic-concurrency check is what catches a
      // concurrent edit and it surfaces below as a conflict rather than a silent overwrite.
      api.patchRepoItem(id, input.ref, input.repositoryId, { version: 0, state: input.state }),
    onSuccess: () => {
      setFailure(null);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['audit-matrix', id] });
      void queryClient.invalidateQueries({ queryKey: ['audit', id] });
    },
    onError: (error: unknown) => {
      setFailure(
        error instanceof ApiClientError ? error.message : 'That answer could not be saved.',
      );
    },
  });

  if (matrix.isPending) {
    return <div style={{ padding: 24, ...NOTE }}>Loading the repository matrix…</div>;
  }

  if (matrix.isError) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ color: 'var(--ba)' }}>
          {matrix.error instanceof ApiClientError
            ? matrix.error.message
            : 'The matrix could not be loaded.'}
        </strong>
        <Link to={`/audits/${id}`} style={{ fontSize: 12.5 }}>
          Back to the workspace
        </Link>
      </div>
    );
  }

  const data: Matrix = matrix.data;

  if (data.rows.length === 0) {
    return (
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)' }}>Repository matrix</h1>
        <div
          style={{
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--r-lg)',
            padding: '30px 22px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            textAlign: 'center',
          }}
        >
          <strong style={{ fontSize: 13 }}>No repository-scoped items in this audit</strong>
          <span style={{ ...NOTE, maxWidth: 380 }}>
            This audit&rsquo;s type covers only project-scoped items, so every answer belongs to
            the project as a whole and the workspace is the right place for all of them.
          </span>
          <Link to={`/audits/${id}`} style={{ fontSize: 12.5 }}>
            Back to the workspace
          </Link>
        </div>
      </div>
    );
  }

  if (data.repositories.length === 0) {
    return (
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)' }}>Repository matrix</h1>
        <div
          style={{
            background: 'var(--sf)',
            border: '1px solid var(--wa-bd)',
            borderRadius: 'var(--r-lg)',
            padding: '30px 22px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            textAlign: 'center',
          }}
        >
          <strong style={{ fontSize: 13, color: 'var(--wa)' }}>No repositories are linked</strong>
          <span style={{ ...NOTE, maxWidth: 400, color: 'var(--wa)' }}>
            {data.rows.length} repository-scoped items cannot be answered until at least one
            repository is linked to the project.
          </span>
        </div>
      </div>
    );
  }

  const gridWidth =
    REF_W + STATEMENT_W + ROLLUP_W + data.repositories.length * CELL_W + 40;

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 13 }}>
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--mu)' }}
          >
            <Link to={`/audits/${id}`}>Workspace</Link>
            <span>›</span>
            <span style={{ color: 'var(--tx)', fontWeight: 600 }}>Repository matrix</span>
          </div>
          <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}>
            Repository matrix
          </h1>
          <span style={NOTE}>
            {data.rows.length} items × {data.repositories.length} repositories. An item&rsquo;s
            overall state is the <b style={{ color: 'var(--tx)' }}>worst</b> of its answers.
          </span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to={`/audits/${id}/evidence`} style={{ fontSize: 12.5 }}>
            Evidence catalog
          </Link>
          <Link to={`/audits/${id}/review`} style={{ fontSize: 12.5 }}>
            Review &amp; submit
          </Link>
        </div>
      </header>

      {failure !== null && (
        <div
          style={{
            background: 'var(--bab)',
            border: '1px solid var(--ba-bd)',
            borderRadius: 'var(--r-lg)',
            padding: '10px 13px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--ba)', flex: 1 }}>{failure}</span>
          <button
            type="button"
            onClick={() => {
              setFailure(null);
              void queryClient.invalidateQueries({ queryKey: ['audit-matrix', id] });
            }}
            style={{
              height: 26,
              padding: '0 9px',
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--ba-bd)',
              background: 'var(--sf)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Reload the grid
          </button>
        </div>
      )}

      {/* the grid — its own horizontal scroll container, so the page never scrolls sideways */}
      <div
        style={{
          border: '1px solid var(--bd)',
          borderRadius: 'var(--r-lg)',
          background: 'var(--sf)',
          overflowX: 'auto',
        }}
      >
        <div style={{ minWidth: gridWidth }}>
          {/* header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              background: 'var(--sunk)',
              borderBottom: '1px solid var(--bd)',
              position: 'sticky',
              top: 0,
            }}
          >
            <div style={{ width: REF_W, flex: `0 0 ${String(REF_W)}px`, padding: '8px 0 8px 13px' }}>
              <span style={{ ...LABEL, fontSize: 9.5 }}>Ref</span>
            </div>
            <div style={{ width: STATEMENT_W, flex: `0 0 ${String(STATEMENT_W)}px`, padding: '8px 0' }}>
              <span style={{ ...LABEL, fontSize: 9.5 }}>Statement</span>
            </div>
            <div style={{ width: ROLLUP_W, flex: `0 0 ${String(ROLLUP_W)}px`, padding: '8px 0' }}>
              <span style={{ ...LABEL, fontSize: 9.5 }}>Roll-up</span>
            </div>
            {data.repositories.map((repo) => (
              <div
                key={repo.id}
                style={{
                  width: CELL_W,
                  flex: `0 0 ${String(CELL_W)}px`,
                  padding: '8px 6px',
                  borderLeft: '1px solid var(--bd)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
                title={repo.pathWithNamespace}
              >
                <span
                  style={{
                    ...MONO,
                    fontSize: 10,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {repo.pathWithNamespace.split('/').pop()}
                </span>
                {repo.isPrimary && (
                  <span style={{ ...LABEL, fontSize: 8.5, color: 'var(--ac)' }}>primary</span>
                )}
              </div>
            ))}
          </div>

          {/* rows */}
          {data.rows.map((row) => (
            <div
              key={row.ref}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                borderBottom: '1px solid var(--bd-soft)',
                minHeight: 38,
              }}
            >
              <div
                style={{
                  width: REF_W,
                  flex: `0 0 ${String(REF_W)}px`,
                  padding: '9px 0 9px 13px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{ ...MONO, fontSize: 11, fontWeight: 600 }}>{row.ref}</span>
              </div>

              <div
                style={{
                  width: STATEMENT_W,
                  flex: `0 0 ${String(STATEMENT_W)}px`,
                  padding: '7px 10px 7px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  minWidth: 0,
                }}
              >
                <MarkerChip obligation={row.obligation} isContextual={row.isContextual} />
                <span
                  style={{
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={row.statement}
                >
                  {row.statement}
                </span>
              </div>

              <div
                style={{
                  width: ROLLUP_W,
                  flex: `0 0 ${String(ROLLUP_W)}px`,
                  padding: '7px 8px 7px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <StateChip state={row.rollUp.state} size="sm" />
                <span style={{ ...MONO, fontSize: 9.5, color: 'var(--mu)' }}>
                  {row.rollUp.verified}/{row.rollUp.considered}
                </span>
              </div>

              {data.repositories.map((repo) => {
                const cell = row.cells.find((c) => c.repositoryId === repo.id) ?? null;
                // primaryRepoOnly: exactly one real cell. The others are struck through,
                // because blank would read as "nobody has answered yet".
                const notAsked = row.primaryRepoOnly && !repo.isPrimary;
                const isEditing =
                  editing !== null && editing.ref === row.ref && editing.repositoryId === repo.id;

                if (notAsked) {
                  return (
                    <div
                      key={repo.id}
                      style={{
                        width: CELL_W,
                        flex: `0 0 ${String(CELL_W)}px`,
                        borderLeft: '1px solid var(--bd-soft)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--bg)',
                      }}
                      title="Asked of the primary repository only"
                    >
                      <span style={{ ...MONO, fontSize: 11, color: 'var(--bd)' }}>╱</span>
                    </div>
                  );
                }

                const mark = cell?.state === null || cell === null ? null : CELL_MARK[cell.state];

                return (
                  <div
                    key={repo.id}
                    style={{
                      width: CELL_W,
                      flex: `0 0 ${String(CELL_W)}px`,
                      borderLeft: '1px solid var(--bd-soft)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 4,
                      position: 'relative',
                    }}
                  >
                    {isEditing ? (
                      <select
                        autoFocus
                        defaultValue={cell?.state ?? 'PENDING'}
                        onBlur={() => {
                          setEditing(null);
                        }}
                        onChange={(event) => {
                          patch.mutate({
                            ref: row.ref,
                            repositoryId: repo.id,
                            state: event.target.value as ItemState,
                          });
                        }}
                        style={{
                          width: '100%',
                          height: 26,
                          fontSize: 11,
                          borderRadius: 'var(--r-sm)',
                          border: '1px solid var(--ac)',
                        }}
                      >
                        {STATES.map((state) => (
                          <option key={state} value={state}>
                            {stateLabel(state)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing({ ref: row.ref, repositoryId: repo.id });
                        }}
                        title={
                          cell?.gapNote !== null && cell?.gapNote !== undefined
                            ? cell.gapNote
                            : cell?.state === null || cell === null
                              ? 'Not answered'
                              : stateLabel(cell.state)
                        }
                        style={{
                          width: 30,
                          height: 24,
                          borderRadius: 'var(--r-sm)',
                          border: `1px solid ${mark?.bd ?? 'var(--bd)'}`,
                          background: mark?.bg ?? 'transparent',
                          color: mark?.fg ?? 'var(--mu)',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {mark?.text ?? '·'}
                      </button>
                    )}
                    {cell?.gapNote !== null && cell?.gapNote !== undefined && (
                      <span
                        style={{
                          position: 'absolute',
                          top: 3,
                          right: 5,
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          background: 'var(--wa)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* legend + the rule, stated where it is being applied */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          style={{
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--r-lg)',
            padding: '11px 13px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            flex: 1,
          }}
        >
          <span style={LABEL}>Cells</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {STATES.map((state) => (
              <span key={state} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span
                  style={{
                    width: 22,
                    height: 20,
                    borderRadius: 'var(--r-sm)',
                    border: `1px solid ${CELL_MARK[state].bd}`,
                    background: CELL_MARK[state].bg,
                    color: CELL_MARK[state].fg,
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {CELL_MARK[state].text}
                </span>
                <span style={NOTE}>{stateLabel(state)}</span>
              </span>
            ))}
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ ...MONO, fontSize: 11, color: 'var(--bd)', width: 22, textAlign: 'center' }}>
                ╱
              </span>
              <span style={NOTE}>not asked — primary only</span>
            </span>
          </div>
        </div>

        <div
          style={{
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderLeft: '2.5px solid var(--ac)',
            borderRadius: 'var(--r-lg)',
            padding: '11px 13px',
            width: 400,
            flex: '0 0 400px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <span style={{ ...LABEL, color: 'var(--ac)' }}>Worst wins</span>
          <span style={NOTE}>
            Rejected &gt; Pending &gt; Partial &gt; Verified. Repositories marked not applicable
            leave the count entirely, so a nine-repository item with eight verified and one
            rejected rolls up to <b style={{ color: 'var(--tx)' }}>Rejected</b> — not to 89%.
            The roll-up column is the server&rsquo;s own answer, not a sum computed in the browser.
          </span>
        </div>
      </div>
    </div>
  );
}
